import { ArrowLeft } from "lucide-react";
import { useCanGoBack, useLocation, useRouter } from "@tanstack/react-router";
import { useBackContext } from "@/lib/dashboard-origin";

/**
 * Screens that already render their own, more specific return control in their
 * header (e.g. "Back to sheet", "All reports"). Showing the shared bar there too
 * produced two stacked back buttons on the same screen, so the shared bar stands
 * down and the screen's own control remains the single entry point.
 */
const SCREENS_WITH_OWN_BACK: RegExp[] = [
  /^\/admin\/web-vitals$/,
  /^\/contractor\/(pay-periods|schedule|shift-claim)$/,
  /^\/contractors\/[^/]+$/,
  /^\/credentials\/[^/]+$/,
  /^\/dar\/(review|[^/]+)$/,
  /^\/guard-tours\/(scan|runs\/[^/]+|[^/]+)$/,
  /^\/incidents\/(new|[^/]+(\/follow-up)?)$/,
  /^\/messages(\/.*)?$/,
  /^\/reports\/(critical|follow-up|incident|tasks|new(\/.*)?|templates(\/.*)?)$/,
  /^\/review\/timecards\/[^/]+$/,
  /^\/schedule$/,
  /^\/settings\/(api-tokens|navigation|security)$/,
  /^\/sites\/(new|[^/]+(\/(edit|checkpoints))?)$/,
];

function hasOwnBackControl(pathname: string) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return SCREENS_WITH_OWN_BACK.some((re) => re.test(path));
}

/**
 * Contextual return action shown when the user arrived from a dashboard or a
 * parent screen. Uses history when possible so no duplicate entries are created
 * and scroll/filters are preserved, and falls back to the parent route on a
 * direct URL visit or refresh.
 */
export function DashboardOriginBar({ className }: { className?: string }) {
  const back = useBackContext();
  const pathname = useLocation({ select: (l) => l.pathname });
  const router = useRouter();
  const canGoBack = useCanGoBack();

  if (!back || hasOwnBackControl(pathname)) return null;
  const label = back.label;



  return (
    <div
      className={
        className ??
        "mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground"
      }
    >
      <button
        type="button"
        onClick={() => {
          if (canGoBack) router.history.back();
          else router.navigate({ to: back.to as never });
        }}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-lg px-2 font-semibold text-muted-foreground transition-colors hover:text-foreground"
        aria-label={`Back to ${label}`}
      >
        <ArrowLeft className="h-4 w-4" />
        Back to {label}
      </button>
      {back.scopeLabel ? (
        <span className="rounded-full border border-border/60 px-2 py-1">
          Scope: {back.scopeLabel}
        </span>
      ) : null}

    </div>
  );
}
